import { useCallback, useEffect, useRef, useState } from 'react';

import {
  SINGLE_REFRESH_BACKOFF_MS,
  SINGLE_REFRESH_COALESCE_MS,
  SINGLE_REFRESH_COOLDOWN_MS,
  SINGLE_REFRESH_TRAILING_QUIET_MS,
} from '../constants';

// This hook manages the single-dealer refresh loop used by the detail drawer.
// It handles coalescing, cooldowns, trailing refreshes, backoff, and global-refresh
// coordination so the UI can refresh one dealer without hammering the backend.
export const useSingleDealerRefresh = ({
  activeDealerRefreshKey,
  isGlobalRefreshInProgress,
  refreshDealerSelections,
  mergeDealerPayload,
  setCurrentRow,
  onRowUpdated,
  setSnackbar,
  setRefreshProgress,
  getAverageDuration,
  getMaxDuration,
  recordSuccessfulCall,
}) => {
  const singleRefreshStateRef = useRef(new Map());
  const scheduleTrailingRefreshRef = useRef(null);
  const runSingleDealerRefreshRef = useRef(null);

  const [autoRefreshInFlight, setAutoRefreshInFlight] = useState(false);

  const recomputeAutoRefreshInFlight = useCallback(() => {
    const states = singleRefreshStateRef.current;
    for (const state of states.values()) {
      if (state?.timerId || state?.inFlightPromise || state?.trailingTimerId) {
        setAutoRefreshInFlight(true);
        return;
      }
    }
    setAutoRefreshInFlight(false);
  }, []);

  const getSingleRefreshState = useCallback((programName, bac) => {
    const key = `${programName}:${String(bac)}`;
    const states = singleRefreshStateRef.current;
    const existing = states.get(key);
    if (existing) return existing;
    const next = {
      key,
      programName,
      bac: String(bac),
      timerId: null,
      trailingTimerId: null,
      inFlightPromise: null,
      trailingNeeded: false,
      lastRequestedAt: 0,
      lastSuccessAt: 0,
      blockedUntil: 0,
      backoffIndex: 0,
      // Force flag applies only to the next scheduled run (manual refresh).
      nextForce: false,
    };
    states.set(key, next);
    return next;
  }, []);

  useEffect(() => {
    const states = singleRefreshStateRef.current;
    return () => {
      for (const state of states.values()) {
        if (state?.timerId) window.clearTimeout(state.timerId);
        if (state?.trailingTimerId) window.clearTimeout(state.trailingTimerId);
      }
      states.clear();
    };
  }, []);

  const scheduleTrailingRefresh = useCallback(
    (state) => {
      if (!state) return;
      if (state.trailingTimerId) return;
      const now = Date.now();
      const sinceLastRequest = now - (state.lastRequestedAt || 0);
      const waitForQuiet = Math.max(0, SINGLE_REFRESH_TRAILING_QUIET_MS - sinceLastRequest);
      const waitForCooldown = Math.max(
        0,
        (state.lastSuccessAt ? state.lastSuccessAt + SINGLE_REFRESH_COOLDOWN_MS - now : 0)
      );
      const waitForBackoff = Math.max(0, (state.blockedUntil || 0) - now);
      const delay = Math.max(waitForQuiet, waitForCooldown, waitForBackoff);
      state.trailingTimerId = window.setTimeout(() => {
        state.trailingTimerId = null;
        // Only run if we've been quiet long enough; otherwise re-arm.
        const quietNow = Date.now() - (state.lastRequestedAt || 0);
        if (quietNow < SINGLE_REFRESH_TRAILING_QUIET_MS) {
          scheduleTrailingRefreshRef.current?.(state);
          recomputeAutoRefreshInFlight();
          return;
        }
        // Fire a coalesced refresh now.
        state.timerId = null;
        state.trailingNeeded = false;
        state.nextForce = false;
        runSingleDealerRefreshRef.current?.(state);
      }, delay);
      recomputeAutoRefreshInFlight();
    },
    [recomputeAutoRefreshInFlight]
  );

  scheduleTrailingRefreshRef.current = scheduleTrailingRefresh;

  const runSingleDealerRefresh = useCallback(
    async (state) => {
      if (!state?.programName || !state?.bac) return null;
      const key = state.key;

      // Option A: while a global refresh is in progress, only allow single-BAC refresh for the active dealer/program.
      if (isGlobalRefreshInProgress && key !== activeDealerRefreshKey) {
        console.debug('[DetailPanel] single-dealer refresh skipped during global refresh (not active dealer)', {
          key,
          activeDealerRefreshKey,
        });
        state.trailingNeeded = false;
        state.nextForce = false;
        recomputeAutoRefreshInFlight();
        return null;
      }

      // Dedup: if a refresh is already running for this dealer/program, don't fire
      // another one now; mark that we should run again after the in-flight call settles.
      if (state.inFlightPromise) {
        state.trailingNeeded = true;
        scheduleTrailingRefreshRef.current?.(state);
        return state.inFlightPromise;
      }

      const now = Date.now();
      const force = Boolean(state.nextForce);
      state.nextForce = false;

      if (!force && state.blockedUntil && now < state.blockedUntil) {
        console.debug('[DetailPanel] single-dealer refresh blocked by backoff', {
          programName: state.programName,
          bac: state.bac,
          blockedForMs: state.blockedUntil - now,
        });
        return null;
      }

      // Cooldown: if we refreshed this key very recently, delay until cooldown expires.
      if (!force && state.lastSuccessAt && now - state.lastSuccessAt < SINGLE_REFRESH_COOLDOWN_MS) {
        const wait = state.lastSuccessAt + SINGLE_REFRESH_COOLDOWN_MS - now;
        state.timerId = window.setTimeout(() => {
          state.timerId = null;
          runSingleDealerRefreshRef.current?.(state);
        }, Math.max(0, wait));
        recomputeAutoRefreshInFlight();
        return null;
      }

      console.debug('[DetailPanel] single-dealer refresh firing', {
        programName: state.programName,
        bac: state.bac,
        source: force ? 'manual' : 'auto',
      });

      const averageDuration = getAverageDuration();
      const startTime = Date.now();
      setRefreshProgress({
        isActive: true,
        progress: 0,
        startTime,
        estimatedDuration: averageDuration,
      });

      const promise = (async () => {
        try {
          const payload = await refreshDealerSelections({ programName: state.programName, bac: state.bac });

          const actualDuration = Date.now() - startTime;
          const maxDuration = getMaxDuration();
          const updatedEstimate = Math.max(averageDuration, maxDuration, actualDuration);

          setRefreshProgress((prev) => ({
            ...prev,
            progress: 100,
            estimatedDuration: updatedEstimate,
          }));

          recordSuccessfulCall(actualDuration);

          setTimeout(() => {
            setRefreshProgress({
              isActive: false,
              progress: 0,
              startTime: null,
              estimatedDuration: null,
            });
          }, 500);

          if (payload && !isGlobalRefreshInProgress && state.key === activeDealerRefreshKey) {
            let merged;
            setCurrentRow((prev) => {
              merged = mergeDealerPayload(prev, payload);
              return merged;
            });
            if (merged) {
              setTimeout(() => onRowUpdated(merged), 0);
            }
          }

          state.lastSuccessAt = Date.now();
          state.blockedUntil = 0;
          state.backoffIndex = 0;
          return payload;
        } catch (err) {
          setRefreshProgress({
            isActive: false,
            progress: 0,
            startTime: null,
            estimatedDuration: null,
          });

          const message = err?.response?.data?.message || err?.message || 'Dealer refresh failed.';
          console.warn('[DetailPanel] single-dealer refresh failed', { key: state.key, message });
          const nextIndex = Math.min(state.backoffIndex + 1, SINGLE_REFRESH_BACKOFF_MS.length - 1);
          state.backoffIndex = nextIndex;
          const backoffMs =
            SINGLE_REFRESH_BACKOFF_MS[nextIndex] || SINGLE_REFRESH_BACKOFF_MS[SINGLE_REFRESH_BACKOFF_MS.length - 1];
          state.blockedUntil = Date.now() + backoffMs;

          if (state.key === activeDealerRefreshKey) {
            setSnackbar({
              open: true,
              message,
              severity: 'warning',
              onClick: null,
              key: Date.now(),
            });
          }
          throw err;
        } finally {
          state.inFlightPromise = null;
          recomputeAutoRefreshInFlight();

          if (state.trailingNeeded) {
            state.trailingNeeded = false;
            scheduleTrailingRefreshRef.current?.(state);
          }
        }
      })();

      state.inFlightPromise = promise;
      recomputeAutoRefreshInFlight();
      return promise;
    },
    [
      activeDealerRefreshKey,
      getAverageDuration,
      getMaxDuration,
      isGlobalRefreshInProgress,
      mergeDealerPayload,
      onRowUpdated,
      recomputeAutoRefreshInFlight,
      recordSuccessfulCall,
      refreshDealerSelections,
      setCurrentRow,
      setRefreshProgress,
      setSnackbar,
    ]
  );

  runSingleDealerRefreshRef.current = runSingleDealerRefresh;

  const requestSingleDealerRefresh = useCallback(
    ({ programName, bac, delayMs = 0, force = false }) => {
      if (!programName || !bac) return null;
      const state = getSingleRefreshState(programName, bac);
      state.lastRequestedAt = Date.now();
      if (force) state.nextForce = true;

      // If something is already scheduled or in-flight, just mark trailing.
      if (state.inFlightPromise || state.timerId || state.trailingTimerId) {
        state.trailingNeeded = true;
        scheduleTrailingRefreshRef.current?.(state);
        recomputeAutoRefreshInFlight();
        return state.inFlightPromise || null;
      }

      const now = Date.now();
      const cooldownWait =
        !force && state.lastSuccessAt && now - state.lastSuccessAt < SINGLE_REFRESH_COOLDOWN_MS
          ? state.lastSuccessAt + SINGLE_REFRESH_COOLDOWN_MS - now
          : 0;
      const effectiveDelay = Math.max(Number(delayMs || 0), SINGLE_REFRESH_COALESCE_MS, cooldownWait);
      state.timerId = window.setTimeout(() => {
        state.timerId = null;
        runSingleDealerRefreshRef.current?.(state);
      }, Math.max(0, effectiveDelay));
      recomputeAutoRefreshInFlight();
      return null;
    },
    [getSingleRefreshState, recomputeAutoRefreshInFlight]
  );

  return {
    autoRefreshInFlight,
    requestSingleDealerRefresh,
  };
};
